// --------------------------------------------------------------
// Created On: 2025-02-28
// Author: Hannah Vaughan
//
// Last Modified: 2025-04-03
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Spinner from "../../components/Spinner/Spinner";
import { API, MAX_SALES_ORDERS_PER_PAGE } from "../../constants/miscellaneous";
import PaginationControl from "../../components/PaginationControl/PaginationControl";
import useApi from "../../hooks/useApi";
import OrderDetailsModal from "./OrderDetailsModal/OrderDetailsModal";
import OrderHistoryForm from "./OrderHistoryForm/OrderHistoryForm";
import OrderHistoryTable from "./OrderHistoryTable/OrderHistoryTable";
import Toast from "../../components/Toast/Toast";
import getApiError from "../../utilities/api/getApiError";

// Page to list and search for a complete sales order history.
export default function OrderHistoryPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<number>(30);
  const [sortColumnIndex, setSortColumnIndex] = useState<number>(1);
  const [sortAscending, setSortAscending] = useState<boolean>(false);
  const [performSearch, setPerformSearch] = useState<boolean>(false);
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [rowCount, setRowCount] = useState<number>(0);

  // Gets paginated order history information based on the search value, date filter, page number,
  // rows per page, column name, and sort direction.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/sales-order/history/search`,
      method: "POST",
      body: {
        dateSearchStart: formatDateSearchStart(dateFilter),
        searchValue: filter.trim(),
        pageNumber: pageNumber,
        rowsPerPage: MAX_SALES_ORDERS_PER_PAGE,
        columnName: getCurrentColumnName(),
        sortDirection: convertSortAscendingBooleanToString(),
      },
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setRowCount(responseBody.rowCount);
        setSalesOrders(responseBody.salesOrders);
        setFailedToLoad(false);
        setErrorMessage("");
      } else {
        setFailedToLoad(true);
        setErrorMessage(await getApiError(response, "Unable to sort order history data."));
      }
      setLoading(false);
    },
    [pageNumber, sortAscending, sortColumnIndex, performSearch]
  );

  // Formats the start search date based on the current date and the provided date filter.
  // 0 days implies a search for all time, hence why null is returned in such an instance.
  function formatDateSearchStart(dateFilter: number): string | null {
    let dateSearchStart = null;
    if (dateFilter !== 0) {
      dateSearchStart = new Date();
      dateSearchStart.setDate(dateSearchStart.getDate() - dateFilter);
    }

    if (dateSearchStart !== null) {
      return dateSearchStart.toISOString();
    } else {
      return null;
    }
  }

  // Gets the human-readable column name for the column that's currently selected for sorting.
  function getCurrentColumnName(): string {
    let columnName = "";
    switch (sortColumnIndex) {
      case 1:
        columnName = "Order Date";
        break;
      case 2:
        columnName = "PO #";
        break;
      case 3:
        columnName = "Sales Order #";
        break;
      case 4:
        columnName = "Grand Total";
        break;
      default:
        columnName = "Order Date";
    }
    return columnName;
  }

  // Converts the sortAscending boolean value to its equivalent string value.
  function convertSortAscendingBooleanToString(): string {
    if (sortAscending) {
      return "ASC";
    } else {
      return "DESC";
    }
  }

  // Changes the currently selected order.
  function updateSelection(selectedId: number): void {
    setSelectedId(selectedId);
    const order = salesOrders.find((order) => order.salesOrderId === selectedId);
    if (order === undefined) {
      setSelectedOrder(null);
    } else {
      setSelectedOrder(order);
    }
  }

  // Updates sorting order.
  function updateSort(newIndex: number): void {
    if (newIndex === sortColumnIndex) {
      setSortAscending((prev) => !prev);
    } else {
      setSortAscending(false);
      setSortColumnIndex(newIndex);
    }
  }

  // Updates the results' current page number for pagination functionality.
  function paginateData(newPageNumber: number): void {
    if (newPageNumber !== pageNumber) {
      setPageNumber(newPageNumber);
    }
  }

  return (
    <div className="container p-4">
      <Spinner loading={loading} />

      {failedToLoad && (
        <Toast
          type="error"
          title="Failed to load sales order history"
          message={errorMessage}
          show={failedToLoad}
          onClose={() => {
            setFailedToLoad(false), setErrorMessage("");
          }}
        />
      )}

      <div className="row">
        <OrderHistoryForm
          rowCount={rowCount}
          filter={filter}
          dateFilter={dateFilter}
          onChangeFilter={(filter) => setFilter(filter)}
          onChangeDateFilter={(dateFilter) => setDateFilter(dateFilter)}
          onPerformSearch={() => setPerformSearch((prev) => !prev)}
        />
        <OrderHistoryTable
          loading={loading}
          salesOrders={salesOrders}
          sortColumnIndex={sortColumnIndex}
          sortAscending={sortAscending}
          onUpdateSort={(newIndex) => updateSort(newIndex)}
          onUpdateSelection={(salesOrderId) => updateSelection(salesOrderId)}
        />
        <PaginationControl
          pageNumber={pageNumber}
          maxPerPage={MAX_SALES_ORDERS_PER_PAGE}
          totalCount={rowCount}
          onClick={(pageNumber) => paginateData(pageNumber)}
        />
        {selectedId > 0 && selectedOrder !== null && (
          <OrderDetailsModal
            orderNumber={selectedOrder.epicorSalesOrderNumber}
            poNumber={selectedOrder.poNumber}
            loading={loading}
            onClose={() => updateSelection(-1)}
            onChangeLoading={(loading) => setLoading(loading)}
            onChangeFailedToLoad={(failedToLoad) => setFailedToLoad(failedToLoad)}
            onChangeErrorMessage={(errorMessage) => setErrorMessage(errorMessage)}
          />
        )}
      </div>
    </div>
  );
}

interface ResponseBody {
  rowCount: number;
  salesOrders: SalesOrder[];
}

interface SalesOrder {
  salesOrderId: number;
  companyId: number;
  userId: number;
  epicorSalesOrderNumber: string;
  poNumber: string;
  grandTotal: number;
  receivedUtc: string | null;
  pickedUtc: string | null;
  shippedUtc: string | null;
}
